import GroupWidgetCard from "@/components/dashboard/group-widget-card";
import { WidgetRenderer } from "@/components/dashboard/widget-renderer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetWidgetData } from "@/hooks";
import { cn } from "@/lib/utils";
import { Widget } from "@/types/dashboard.types";
import {
  BarChart2,
  BarChart3,
  CalendarDays,
  Edit,
  Grid,
  GripVertical,
  LineChart,
  Map as MapIcon,
  PieChart,
  ScatterChart,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const WIDGET_TYPE_ICONS = {
  bar: BarChart3,
  line: LineChart,
  pie: PieChart,
  calendar: CalendarDays,
  "calendar-heatmap": CalendarDays,
  histogram: BarChart2,
  scatter: ScatterChart,
  card: BarChart3,
  map: MapIcon,
  crosstab: Grid,
  cct: Grid,
};

interface DraggableWidgetProps {
  widget: Widget;
  onEdit: (widgetId: string) => void;
  onDelete: (widgetId: string) => void;
  isInteractive?: boolean;
  allWidgets?: Widget[];
}

export default function DraggableWidget({
  widget,
  onEdit,
  onDelete,
  isInteractive = true,
  allWidgets,
}: DraggableWidgetProps) {
  const { t } = useTranslation();
  const IconComponent =
    WIDGET_TYPE_ICONS[
      widget.visualizationType as keyof typeof WIDGET_TYPE_ICONS
    ] || BarChart3;

  const {
    data: widgetDataR,
    isLoading: dataLoading,
    error: dataError,
  } = useGetWidgetData(widget.id);
  const widgetData = widgetDataR?.data;

  const shouldShowPreview = !widgetData && !dataLoading && !dataError;

  // Special handling for group widgets
  if (widget.visualizationType === "group") {
    return (
      <GroupWidgetCard
        widget={widget}
        onEdit={onEdit}
        onDelete={onDelete}
        isInteractive={isInteractive}
        allWidgets={allWidgets}
      />
    );
  }

  return (
    <Card className="flex w-full h-full flex-col transition-shadow duration-200 group hover:shadow-md">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {/* Drag handle */}
            {isInteractive && (
              <div className="drag-handle cursor-grab rounded p-1 active:cursor-grabbing hover:bg-muted">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <IconComponent className="h-5 w-5 flex-shrink-0 text-primary" />
            <CardTitle className="min-w-0 flex-1 truncate text-base">
              {widget.title || t("processManagement.untitledWidget")}
            </CardTitle>
          </div>
          {isInteractive && (
            <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                onClick={() => onEdit(widget.id)}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => onDelete(widget.id)}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex flex-col flex-grow pt-0 min-h-0">
        <div className="relative flex-grow rounded-lg border bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col min-h-0">
          <WidgetRenderer
            widget={widget}
            data={widgetData}
            loading={dataLoading}
            error={dataError?.message}
            isPreview={shouldShowPreview}
          />
        </div>
      </CardContent>
    </Card>
  );
}
